import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  getArticles,
  createArticle,
  updateArticle,
  deleteArticle,
} from '../../lib/supabase';

interface ArticleFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tags: string;
  is_published: boolean;
  published_at: string;
}

const ArticlesManager: React.FC = () => {
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState<any>(null);
  const [formData, setFormData] = useState<ArticleFormData>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    tags: '',
    is_published: false,
    published_at: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const data = await getArticles(true);
      setArticles(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (article?: any) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt || '',
        content: article.content,
        tags: Array.isArray(article.tags) ? article.tags.join(', ') : '',
        is_published: article.is_published,
        published_at: article.published_at?.split('T')[0] || new Date().toISOString().split('T')[0],
      });
    } else {
      setEditingArticle(null);
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        tags: '',
        is_published: false,
        published_at: new Date().toISOString().split('T')[0],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingArticle(null);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      const articleData = {
        ...formData,
        tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
        published_at: formData.is_published ? formData.published_at : null,
      };

      if (editingArticle) {
        await updateArticle(editingArticle.id, articleData);
      } else {
        await createArticle(articleData);
      }

      await loadArticles();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      await deleteArticle(id);
      await loadArticles();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleInputChange = (field: keyof ArticleFormData, value: any) => {
    setFormData({ ...formData, [field]: value });
    
    // Auto-generate slug from title
    if (field === 'title' && !editingArticle) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Impact", "Arial Black", sans-serif',
            fontWeight: 900,
            fontSize: '2rem',
            textTransform: 'uppercase',
            color: '#1a1a1a',
          }}
        >
          Articles
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#dc143c',
            color: 'white',
            borderRadius: 0,
            fontFamily: '"Arial Black", sans-serif',
            fontWeight: 700,
            textTransform: 'uppercase',
            '&:hover': {
              bgcolor: '#b01030',
            }
          }}
        >
          New Article
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 0,
            border: '2px solid #dc143c',
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 0, border: '2px solid #dc143c', bgcolor: 'white' }}>
        <Table sx={{ bgcolor: 'white' }}>
          <TableHead sx={{ bgcolor: '#1a1a1a' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Published Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Views</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Tags</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ bgcolor: 'white' }}>
            {articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No articles yet
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => (
                <TableRow
                  key={article.id}
                  sx={{
                    bgcolor: 'white',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    }
                  }}
                >
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif', fontWeight: 600 }}>{article.title}</TableCell>
                  <TableCell>
                    {article.is_published ? (
                      <Chip label="Published" size="small" color="success" />
                    ) : (
                      <Chip label="Draft" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>
                    {article.published_at ? formatDate(article.published_at) : '-'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif', fontWeight: 600 }}>{article.views_count || 0}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {article.tags?.slice(0, 2).map((tag: string, idx: number) => (
                        <Chip key={idx} label={tag} size="small" />
                      ))}
                      {article.tags?.length > 2 && (
                        <Chip label={`+${article.tags.length - 2}`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => window.open(`/article/${article.slug}`, '_blank')}
                      title="View"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(article)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(article.id)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'white',
            borderRadius: 0,
            border: '3px solid #dc143c',
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: '#1a1a1a',
          color: 'white',
          fontFamily: '"Arial Black", sans-serif',
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          {editingArticle ? 'Edit Article' : 'New Article'}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'white', pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Slug"
              value={formData.slug}
              onChange={(e) => handleInputChange('slug', e.target.value)}
              required
              fullWidth
              helperText="URL-friendly version of the title"
            />
            <TextField
              label="Excerpt"
              value={formData.excerpt}
              onChange={(e) => handleInputChange('excerpt', e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="Content (Markdown)"
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              required
              multiline
              rows={10}
              fullWidth
            />
            <TextField
              label="Tags (comma-separated)"
              value={formData.tags}
              onChange={(e) => handleInputChange('tags', e.target.value)}
              fullWidth
              helperText="e.g., AI, Python, Machine Learning"
            />
            <TextField
              label="Published Date"
              type="date"
              value={formData.published_at}
              onChange={(e) => handleInputChange('published_at', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_published}
                  onChange={(e) => handleInputChange('is_published', e.target.checked)}
                />
              }
              label="Published"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'white', p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#dc143c',
              borderRadius: 0,
              fontFamily: '"Arial Black", sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#dc143c',
              color: 'white',
              borderRadius: 0,
              fontFamily: '"Arial Black", sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
              '&:hover': {
                bgcolor: '#b01030',
              }
            }}
          >
            {editingArticle ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArticlesManager;